import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabaseAdmin } from './supabase'

type AuthContextType = {
  user: User | null
  session: Session | null
  loading: boolean
  isPlatformAdmin: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)

  useEffect(() => {
    supabaseAdmin.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        checkPlatformAdmin(session.user.id)
      }
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabaseAdmin.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        checkPlatformAdmin(session.user.id)
      } else {
        setIsPlatformAdmin(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const checkPlatformAdmin = async (userId: string) => {
    const { data } = await supabaseAdmin
      .from('platform_admins')
      .select('id')
      .eq('user_id', userId)
      .single()
    setIsPlatformAdmin(!!data)
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabaseAdmin.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const signOut = async () => {
    const { error } = await supabaseAdmin.auth.signOut()
    if (error) throw error
  }

  return (
    <AuthContext.Provider
      value={{ user, session, loading, isPlatformAdmin, signIn, signOut }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
